import React, { useState } from "react"
import { MenuItem } from "@components"
import style from "./drilldown.mod.scss"
import { motion } from "framer-motion"
import uniqid from "uniqid"
import Button from "./button"

const Drilldown = ({ items, isVisible, closeSidebar }) => {
  const [curDepth, setCurDepth] = useState(0)
  const [curChildMenu, setCurChildMenu] = useState([])
  const [curChildHeading, setCurChildHeading] = useState("")
  const [prevChildMenu, setPrevChildMenu] = useState([])
  const [prevChildHeading, setPrevChildHeading] = useState("")
  const [isNestedMenuVisible, setIsNestedMenuVisible] = useState(false)

  const returnToIndex = (depth = 0, prevMenu = [], prevHeading = "") => {
    if (depth > 1) {
      setCurChildMenu(prevMenu)
      setCurChildHeading(prevHeading)
      setCurDepth(1)
      setIsNestedMenuVisible(true)
    } else {
      setCurChildMenu([])
      setCurChildHeading("")
      setCurDepth(0)
    }
  }

  const handleSetChildMenu = (items, heading = "", depth) => {
    setCurChildMenu(items)
    setCurChildHeading(heading)
    setCurDepth(depth)
    setIsNestedMenuVisible(true)
  }

  const handleSetPrevChildMenu = (curMenu, curHeading = "") => {
    setPrevChildMenu(curMenu)
    setPrevChildHeading(curHeading)
    setIsNestedMenuVisible(false)
  }

  const flattenedMegaMenuItems = ({ path, title, id, columns }, i, depth) => {
    const html = [
      <MenuItem
        key={`drilldown__item-${id}-${i}`}
        dataKey={`drilldown__item-${id}-${i}`}
        link={{ path }}
        modifier={"drilldown"}
        displayText={title}
        vertical
        tabIndex={depth === 0 ? 0 : -1}
        index={i}
        onClick={() => close()}
        isMobile
      />
    ]

    columns.every(({ heading, collections, tile }, ii) => {
      if (tile) return
      html.push(
        <MenuItem
          key={`drilldown__item-${uniqid()}-ii`}
          dataKey={`drilldown__item-${i}-${ii}`}
          modifier={"drilldown"}
          link={{ path }}
          displayText={heading}
          onClick={() => {
            handleSetPrevChildMenu(collections, heading)
            handleSetChildMenu(collections, heading, 1)
          }}
          isButton
          unstyled
          showIcon
          vertical
          tabIndex={depth === 0 ? 0 : -1}
          index={ii}
          isMobile
        />
      )
    })

    return html
  }

  const megaMenuItems = (item, i, depth) => {
    const html = []
    const columnsWithCollections = item.columns.filter(column => !column.tile)
    let customLink = item.link?.link?.path || item.path
    html.push(
      <MenuItem
        key={`drilldown__item-${item.id}-${i}`}
        dataKey={`drilldown__item-${item.id}-${i}`}
        modifier={"drilldown"}
        link={{ path: customLink, url: item.link?.url }}
        displayText={item.title}
        onClick={() => {
          columnsWithCollections.forEach(column =>
            column.collections.length > 0
              ? (column.hasChildren = true)
              : (column.hasChildren = false)
          )

          handleSetPrevChildMenu([...columnsWithCollections], item.title)
          handleSetChildMenu([...columnsWithCollections], item.title, 1)
        }}
        isButton
        unstyled
        showIcon
        vertical
        tabIndex={depth === 0 ? 0 : -1}
        index={i}
        isMobile
      />
    )

    return html
  }

  const learnMenuItems = (item, i, depth) => {
    const html = []
    const { groups } = item

    html.push(
      <MenuItem
        key={`drilldown__item-${item.id}-${i}`}
        dataKey={`drilldown__item-${item.id}-${i}`}
        modifier={"drilldown"}
        link={{ path: item.path }}
        displayText={"Learn Centre"}
        onClick={() => {
          handleSetPrevChildMenu(groups, "Learn Centre")
          handleSetChildMenu(groups, "Learn Centre", 1)
        }}
        isButton
        unstyled
        showIcon
        vertical
        tabIndex={depth === 0 ? 0 : -1}
        index={i}
        isMobile
      />
    )

    return html
  }

  const close = () => {
    closeSidebar()
    returnToIndex()
  }

  const variants = {
    open: {
      display: "block",
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.07, delayChildren: 0.2 }
    },
    closed: {
      display: "none",
      opacity: 0,
      y: -16,
      transition: { staggerChildren: 0.05, staggerDirection: -1 }
    }
  }

  return (
    <div className={style.drilldown}>
      <motion.ul
        id={`drilldown__list--index`}
        initial={"closed"}
        animate={isVisible && curDepth === 0 ? "open" : "closed"}
        variants={variants}
        className={style.drilldown__list}
      >
        {items.map((item, i) => {
          const {
            id,
            path,
            title,
            treeChildren = [],
            isMegaMenu = false,
            isLearnMenu = false,
            groups
          } = item

          const hasChildren = treeChildren.length > 0

          if (isLearnMenu) {
            return learnMenuItems(item, i, curDepth)
          }

          if (isMegaMenu && path === "/products/") {
            return flattenedMegaMenuItems(item, i, curDepth)
          }

          if (isMegaMenu && path !== "/products/") {
            return megaMenuItems(item, i, curDepth)
          } else
            return (
              <MenuItem
                key={`drilldown__item-${id}-${i}`}
                dataKey={`drilldown__item-${id}-${i}`}
                modifier={"drilldown"}
                link={{ path }}
                onClick={
                  hasChildren
                    ? e => {
                        e.preventDefault()
                        handleSetPrevChildMenu([...treeChildren], title)
                        handleSetChildMenu([...treeChildren], title, 1)
                      }
                    : () => close()
                }
                isButton={hasChildren}
                unstyled
                displayText={title}
                showIcon={isMegaMenu || hasChildren}
                vertical
                tabIndex={curDepth === 0 ? 0 : -1}
                index={i}
                isMobile
              />
            )
        })}
      </motion.ul>

      {curChildMenu.length > 0 && (
        <motion.ul
          initial={"closed"}
          animate={
            isVisible && isNestedMenuVisible && curDepth > 0 ? "open" : "closed"
          }
          variants={{
            open: {
              opacity: 1,
              y: 0,
              transition: {
                staggerChildren: 0.07,
                delayChildren: 0.5
              }
            },
            closed: {
              opacity: 0,
              y: -16,
              transition: {
                staggerChildren: 0.05,
                staggerDirection: -1
              }
            }
          }}
          className={style.drilldown__child}
        >
          {curChildHeading !== "" && (
            <motion.li className={style.drilldown__subheading}>
              {curDepth > 0 &&
              (curChildHeading === "Learn Centre" ||
                curChildMenu[0]?.__typename === "DatoCmsLearnMenuMobileItem" ||
                curChildMenu[0]?.__typename ===
                  "DatoCmsLearnMenuMobileGroup") ? (
                <>
                  <MenuHeading
                    heading={curChildHeading}
                    to={
                      curChildMenu[0]?.groupLink?.itemLink?.path ||
                      `/learn/${curChildMenu[0]?.groupLink?.itemLink?.slug}/`
                    }
                    groupLink={curChildMenu[0]?.groupLink}
                    onClose={close}
                  />
                </>
              ) : (
                <h4>{curChildHeading}</h4>
              )}
            </motion.li>
          )}
          {curChildMenu[0]?.__typename === "DatoCmsLearnMenuMobileItem" ||
          curChildMenu[0]?.__typename === "DatoCmsLearnMenuMobileGroup" ||
          curChildMenu[0]?.groupItems
            ? curChildMenu.map((item, i) => {
                // Handle top-level groups
                if (item.groupItems) {
                  const { heading, groupItems, groupLink } = item
                  return (
                    <MenuItem
                      key={`drilldown__item-${i}`}
                      dataKey={`drilldown__item-${i}`}
                      modifier={"drilldown"}
                      displayText={heading}
                      onClick={() => {
                        handleSetPrevChildMenu(curChildMenu, curChildHeading)
                        handleSetChildMenu(
                          groupItems.map(item => ({
                            ...item,
                            groupLink,
                            __typename: "DatoCmsLearnMenuMobileItem"
                          })),
                          heading,
                          curDepth + 1
                        )
                      }}
                      isButton
                      unstyled
                      showIcon
                      vertical
                      tabIndex={0}
                      index={i}
                      isMobile
                    />
                  )
                }

                // Handle individual items
                const { itemLink, heading, id } = item
                let displayText = heading
                let safePath = "/"

                if (itemLink) {
                  displayText = heading || itemLink.title || itemLink.name
                  if (itemLink.path) {
                    safePath = itemLink.path
                  } else if (itemLink.parentCategory) {
                    safePath = `/learn/${itemLink.parentCategory[0].slug}/${itemLink.slug}/`
                  } else if (itemLink.slug) {
                    safePath = `/learn/${itemLink.slug}/`
                  }
                }

                return (
                  <MenuItem
                    key={`drilldown__item-${id || i}`}
                    dataKey={`drilldown__item-${id || i}`}
                    modifier={"drilldown"}
                    link={{ path: safePath }}
                    displayText={displayText}
                    onClick={close}
                    vertical
                    tabIndex={0}
                    index={i}
                    isMobile
                  />
                )
              })
            : curChildMenu.map(
                (
                  {
                    path,
                    slug,
                    title,
                    name,
                    parentCategory,
                    isExternal = false,
                    hasChildren = false,
                    heading,
                    collections,
                    id,
                    itemLink
                  },
                  iii
                ) => {
                  let safePath = path
                  let displayText = title || name || heading

                  // Handle items with itemLink (like in Information menu)
                  if (itemLink) {
                    displayText = heading || itemLink.title || itemLink.name
                    if (itemLink.path) {
                      safePath = itemLink.path
                    } else if (itemLink.parentCategory) {
                      safePath = `/learn/${itemLink.parentCategory[0].slug}/${itemLink.slug}/`
                    } else if (itemLink.slug) {
                      safePath = `/learn/${itemLink.slug}/`
                    }
                  } else if (parentCategory !== undefined) {
                    safePath = `/${parentCategory.slug}/${slug}/`
                  }

                  if (
                    id &&
                    id.includes("LearnSubcategory") &&
                    parentCategory?.length > 0
                  ) {
                    safePath = `/learn/${parentCategory[0].slug}/${slug}/`
                  }

                  return (
                    <MenuItem
                      key={`drilldown__child-item-${id}-${iii}`}
                      modifier={"drilldown"}
                      dataKey={`drilldown__child-item-${id}-${iii}`}
                      link={{ path: safePath }}
                      displayText={displayText}
                      vertical
                      index={iii}
                      tabIndex={curDepth > 1 ? 0 : -1}
                      onClick={
                        hasChildren
                          ? e => {
                              e.preventDefault()
                              setIsNestedMenuVisible(false)
                              handleSetChildMenu([...collections], heading, 2)
                            }
                          : () => close()
                      }
                      isExternal={isExternal}
                      isButton={hasChildren}
                      showIcon={hasChildren}
                      unstyled
                      isMobile
                    />
                  )
                }
              )}

          <MenuItem
            modifier={"drilldown"}
            key={`drilldown__child-item-return-${curChildHeading}`}
            dataKey={`drilldown__child-item-return-${curChildHeading}`}
            displayText={"Back"}
            backIcon
            link={{ path: "/" }}
            onClick={e => {
              e.preventDefault()
              setIsNestedMenuVisible(false)
              returnToIndex(curDepth, prevChildMenu, prevChildHeading)
            }}
            isButton
            unstyled
            vertical
            index={100}
            tabIndex={curDepth > 1 ? 0 : -1}
            isMobile
          />
        </motion.ul>
      )}
    </div>
  )
}

const MenuHeading = ({ heading, to, groupLink, onClose }) => {
  return (
    <div className={style.learn__heading}>
      <h4>{heading}</h4>
      {to && (
        <Button short secondary to={to} onClick={onClose}>
          View all
        </Button>
      )}
    </div>
  )
}

export default Drilldown
